import React, { useState, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaFilePdf, FaFileWord, FaFileImage, FaFile } from '../../../icons';
import './DragDropFileUpload.scss';

interface DragDropFileUploadProps {
  onFilesSelected: (files: FileList) => void;
  currentFiles?: FileList | null;
  error?: string;
}

const DragDropFileUpload: React.FC<DragDropFileUploadProps> = ({
  onFilesSelected,
  currentFiles,
  error
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (indexToRemove: number) => {
    if (!currentFiles) return;

    const filesArray = Array.from(currentFiles);
    filesArray.splice(indexToRemove, 1);

    // Create new FileList
    const dt = new DataTransfer();
    filesArray.forEach(file => dt.items.add(file));
    onFilesSelected(dt.files);
  };

  const getFileIcon = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return <FaFilePdf className="text-danger" />;
      case 'doc': case 'docx': return <FaFileWord className="text-primary" />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <FaFileImage className="text-success" />;
      default: return <FaFile className="text-secondary" />;
    }
  };

  return (
    <div className="drag-drop-upload">
      <div
        className={`upload-area ${isDragOver ? 'drag-over' : ''} ${error ? 'error' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <div className="upload-content">
          <FaCloudUploadAlt className="upload-icon" />
          <p className="upload-text">
            <strong>Click to upload</strong> or drag and drop
          </p>
          <p className="upload-subtitle">
            PDF, DOC, TXT, JPG, PNG (max 10MB each)
          </p>
        </div>
      </div>

      {error && <div className="upload-error">{error}</div>}

      {currentFiles && currentFiles.length > 0 && (
        <div className="uploaded-files">
          <h6>Attached Files:</h6>
          {Array.from(currentFiles).map((file, index) => (
            <div key={index} className="file-item">
              <div className="file-info">
                {getFileIcon(file)}
                <span className="file-name">{file.name}</span>
                <span className="file-size">
                  ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <button
                type="button"
                className="remove-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                title="Remove file"
              >
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DragDropFileUpload;